import React, { useEffect, useMemo, useRef, useState } from 'react';
import { adminApi, adminAuth, adminInboxApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import {
  LogOut, ShieldCheck, Lock, BookOpen, ClipboardCheck,
  Users, DollarSign, Cpu, KeyRound, Database, Wallet, Shield, ShieldOff, UserPlus, Activity, Globe, Globe2, HardDrive, Crown, BarChart3, Layout, Layers, Pencil, UserCog, Coins, HelpCircle, PlayCircle, FlaskConical, Hash, TrendingUp, Archive, LifeBuoy, Skull, Tv, Mail, Search as SearchIcon, ArrowDownRight, Landmark, Package, Scale, ArrowRightLeft, Megaphone, Youtube, Newspaper, Headphones, Menu, LayoutGrid, Settings, Server, Compass, FileText, MessageSquare, GitBranch, Zap, Image, Mic,
} from 'lucide-react';
import { toast } from 'sonner';

// 2026-10 — "Rail icon consistency" spark: give the group switcher
// (Hub/Finance/Users/Operations/Marketing/IT) the same small
// monochrome-outlined icon treatment used on the public landing
// page's nav rail, instead of plain text pills.
const GROUP_ICONS = {
  admin:        LayoutGrid,
  finance:      Wallet,
  users:        Users,
  operations:   Settings,
  editing:      Pencil,
  marketing:    Megaphone,
  it:           Server,
  indie_reader: BookOpen,
};
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import GuidedTour from '@/components/GuidedTour';
import AdminNotificationsBell from '@/components/admin/AdminNotificationsBell';
import AdminMobileGate from '@/components/admin/AdminMobileGate';
import AdminSessionWarning from '@/components/admin/AdminSessionWarning';

import UsersPanel from './admin/UsersPanel';
import ArchivedUsersPanel from './admin/ArchivedUsersPanel';
import PricingPanel from './admin/PricingPanel';
import ModelsPanel from './admin/ModelsPanel';
import LoomSettingsPanel from './admin/LoomSettingsPanel';         // Oct-2026 — per-project Loom config
import CoverArtPanel from './admin/CoverArtPanel';                 // Oct-2026 — cover art upload + DOCX regen
import KeysPanel from './admin/KeysPanel';
import SecretsVaultPanel from './admin/SecretsVaultPanel';
import CollectionsPanel from './admin/CollectionsPanel';
import BudgetPanel from './admin/BudgetPanel';
import RolesPanel from './admin/RolesPanel';
import MembershipsPanel from './admin/MembershipsPanel';
import AuditPanel from './admin/AuditPanel';
import AdminSecurityPanel from './admin/AdminSecurityPanel';
import SeoPanel from './admin/SeoPanel';
import BlogAdminPanel from './admin/BlogAdminPanel';   // 2026-10
import CapacityPanel from './admin/CapacityPanel';
import AccountingPanel from './admin/AccountingPanel';
import SubscriptionPlansPanel from './admin/SubscriptionPlansPanel';
import UnitEconomicsPanel from './admin/UnitEconomicsPanel';
import ShelfAttributionPanel from './admin/ShelfAttributionPanel';   // Feb-2026 sales attribution
import SearchVisibilityPanel from './admin/SearchVisibilityPanel';   // Oct-2026 Video GEO + YouTube
import AiCachingAdminPanel from './admin/AiCachingAdminPanel';       // Prompt caching config
import TeamsPanel from './admin/TeamsPanel';
import FinancialStatementsPanel from './admin/FinancialStatementsPanel';
import BookkeepingPanel from './admin/BookkeepingPanel';
import LandingPanel from './admin/LandingPanel';
import WhatWeOfferPanel from './admin/WhatWeOfferPanel';
import EditingPanel from './admin/EditingPanel';
import BulkNovelEvalPanel from './admin/BulkNovelEvalPanel';
import ProfilePanel from './admin/ProfilePanel';
import RoyaltiesPanel from './admin/RoyaltiesPanel';
import AdminMarketingBookings from './admin/AdminMarketingBookings';
import HelpManualPanel from './admin/HelpManualPanel';
import DemoFunnelPanel from './admin/DemoFunnelPanel';
import QAPanel from './admin/QAPanel';
import IsbnAdminPanel from './admin/IsbnAdminPanel';
import AdminUsersPanel from './admin/AdminUsersPanel';
import AuthorRoyaltyTiersPanel from './admin/AuthorRoyaltyTiersPanel';  // iter-p3-y
import BroadcastsAdminPanel from './admin/BroadcastsAdminPanel';  // iter-p3-aa
import ClarityAdminPanel from './admin/ClarityAdminPanel';
import AnalyticsPanel from './admin/AnalyticsPanel';
import StripeModeBanner from '@/components/admin/StripeModeBanner';
import HumanServicesAdminPanel from './admin/HumanServicesAdminPanel';
import StarterTemplatesAdminPanel from './admin/StarterTemplatesAdminPanel';
import GenreVariantsAdminPanel from './admin/GenreVariantsAdminPanel';
import TrainingAdminPanel from './admin/TrainingAdminPanel';   // iter-training-v-ach-coach
import TeleplayQAPanel from './admin/TeleplayQAPanel';   // iter-studio-dashboard-3
import SeoConfigAdminPanel from './admin/SeoConfigAdminPanel';   // iter-p2-b
import FeatureVideosAdminPanel from './admin/FeatureVideosAdminPanel';   // iter-p2-d
import TrainingBundlesPanel from './admin/TrainingBundlesPanel';   // iter-training-bundles
import MediaDevelopmentPanel from './admin/MediaDevelopmentPanel';
import MediaVaultPanel from './admin/MediaVaultPanel';
import MarketingVenueCatalogPanel from './admin/MarketingVenueCatalogPanel';
import AuthorRankingsPanel from './admin/AuthorRankingsPanel';
import IndieReaderAdminPanel from './admin/IndieReaderAdminPanel';  // iter55z-audit round 9
import ReaderSyncPanel from './admin/ReaderSyncPanel';              // Feb-2026 catalog sync health
import EmailWorkersCliPanel from './admin/EmailWorkersCliPanel';  // iter55z-audit round 9 (extras 2)
import ReaderReviewQueuePanel from './admin/ReaderReviewQueuePanel';  // Feb-2026 editor review queue
import EditorWorkbenchPanel from './admin/EditorWorkbenchPanel';  // Sep-2026 — embedded Publishing Editor Workbench
import EditorialReviewInboxPanel from './admin/EditorialReviewInboxPanel';
import AdminSeatsPanel from './admin/AdminSeatsPanel';  // Feb-2026 editor seats
import CompanionInboxPanel from './admin/CompanionInboxPanel';  // Feb-2026 companion mailbox
import AdminInboxPanel from './admin/AdminInboxPanel';  // iter-inbox-pin — Administrator mailbox as a tab
import RequestLogPanel from './admin/RequestLogPanel';  // Feb-2026 — build request log export
import WriterFeedbackPanel from './admin/WriterFeedbackPanel';  // iter-feedback — writer suggestion inbox
import AdminEmailLogPage from './admin/AdminEmailLogPage';  // Feb-2026 — outbound email send log (pinned tab)
import ItManagementPanel from './admin/ItManagementPanel';  // Feb-2026 — IT Management (scope-isolated)
import TaxCenterPanel from './admin/TaxCenterPanel';
import OhasCertifyPanel from './admin/OhasCertifyPanel';
import WipoContractsPanel from './admin/WipoContractsPanel';
import AuthorAgreementsPanel from './admin/AuthorAgreementsPanel';
import IsbnBankPanel from './admin/IsbnBankPanel';
import EcoFilingPanel from './admin/EcoFilingPanel';
import { AdminToolsDrawer } from './admin/AdminToolsDrawer';
import AdminLoomManuscriptPicker from '@/components/admin/AdminLoomManuscriptPicker';   // 2026-08 — editor Loom bridge
import AdminGuidePanel from './admin/AdminGuidePanel';
import AdminOverviewPanel from './admin/AdminOverviewPanel';   // Feb-2026 · Admin overview
import CompliancePanel from './admin/CompliancePanel';         // Feb-2026 · Compliance & Licenses
import PermissionsPanel from './admin/PermissionsPanel';       // Feb-2026 · Dedicated permissions matrix
import AgingReportPanel from './admin/AgingReportPanel';       // Feb-2026 · AR/AP aging
import InventoryPanel from './admin/InventoryPanel';           // Feb-2026 · Inventory volume
import AutoLedgerHealthPanel from './admin/AutoLedgerHealthPanel'; // Feb-2026 · Auto-post health
import BankReconciliationPanel from './admin/BankReconciliationPanel'; // Feb-2026 · Bank recon
import AdminHelpButton from '@/components/admin/AdminHelpDrawer';
import PanelEmailButton from '@/components/admin/PanelEmailButton';
import TipsAdminPanel from './admin/TipsAdminPanel';
import RevenueFeaturesPanel from './admin/RevenueFeaturesPanel';  // Feb-2026 revenue lifts
import CanvaAdminPanel from './admin/CanvaAdminPanel';  // Feb-2026 Canva cover analytics
import ApprovalQueuePanel from './admin/ApprovalQueuePanel';
import { StripePaymentsPanel } from './admin/StripePaymentsPanel';
import RetroactiveReviewPanel from './admin/RetroactiveReviewPanel';
import ContinuityDriftPanel from './admin/ContinuityDriftPanel';
import PlotBuilderAdminPanel from './admin/PlotBuilderAdminPanel';
import AdminThemeSwitcher, { applyAdminTheme, readAdminTheme } from './admin/AdminThemeSwitcher';
import AudiobookVoicesPanel from './admin/AudiobookVoicesPanel';   // iter-ab2
import ElevenLabsNarrationPanel from './admin/ElevenLabsNarrationPanel';  // ElevenLabs narrator studio
import ManuscriptInventoryPanel from './admin/ManuscriptInventoryPanel';  // manuscript acquisition pipeline
import ManuscriptExportPanel from './admin/ManuscriptExportPanel';        // bulk ZIP export
import ReferralAdminPanel from './admin/ReferralAdminPanel';       // Feb-2026 referral dashboard
import ComparisonAdminPanel from './admin/ComparisonAdminPanel';   // Feb-2026 comparison pages
import FreeTierAdminPanel from './admin/FreeTierAdminPanel';       // Feb-2026 free tier closure
import { tabsForGroup, defaultTabForGroup, GROUP_META, tabsForScopes } from './admin/adminTabGroups';
import { CacheStatusDot } from '../components/chrome/StatusBar';

export default function AdminPage({ group }) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [authed, setAuthed] = useState(false);
  // iter-click-fix — if we were just bounced here from a password-reset
  // confirmation for a specific user (?user=David), default the sign-in
  // dropdown to that account instead of always "Administrator", so the
  // operator isn't silently trying the new password against the wrong user.
  const [user, setUser] = useState(() => {
    const u = searchParams.get('user');
    return u === 'David' ? 'David' : 'Administrator';
  });
  const [pwd, setPwd] = useState('');
  const [busy, setBusy] = useState(false);
  // 2026-10 — Mobile Staff biometric lock. Desktop is never gated;
  // on a phone/tablet, if any admin has opted in, the real sign-in
  // form below is hidden behind <AdminMobileGate/> until it passes
  // (or the 5-min pre-auth cookie from an earlier pass is still valid).
  const [gateChecking, setGateChecking] = useState(true);
  const [gateNeeded, setGateNeeded] = useState(false);
  useEffect(() => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;
    if (!isMobile) { setGateChecking(false); return; }
    (async () => {
      try {
        const status = await adminApi.mobileLockStatus();
        if (!status.enabled) return;
        const chk = await adminApi.mobileLockGateCheck();
        if (!chk.passed) setGateNeeded(true);
      } catch {
        // Fail-open — a network hiccup here must never lock an admin
        // out of their own console.
      } finally { setGateChecking(false); }
    })();
  }, []);
  // Sep-2026 · apply the admin theme on FIRST paint (including the
  // unauthed login screen) so the Fluent/Windows canvas isn't gated
  // behind sign-in. `AdminThemeSwitcher` still keeps it in sync
  // after login.
  useEffect(() => { applyAdminTheme(readAdminTheme()); }, []);
  // iter-51y — track the visible tab so the Admin Help button can scroll
  // straight to the matching section.
  // iter55f73 — the initial tab now respects the current admin group so
  // deep-linking into /admin/finance opens the first finance tab instead
  // of falling through to Users.
  const [tab, setTab] = useState(group ? defaultTabForGroup(group) : 'users');
  // Deep-linkable tabs. Resolve the group and `?tab=` target in one
  // effect so a group reset can never overwrite an Overview deep-link.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const wanted = params.get('tab');
    const allowedForGroup = group ? tabsForGroup(group) : null;
    setTab(wanted && (!allowedForGroup || allowedForGroup.has(wanted))
      ? wanted
      : (group ? defaultTabForGroup(group) : 'users'));
  }, [group, location.search]);
  // iter-51z — scope-aware gating.
  const [scopes, setScopes] = useState(null);  // null = unknown yet, [] = no scopes
  // iter55f73 — Which tabs are visible on this admin page. Undefined
  // `group` means the legacy full-console view (kept as `/admin` hub
  // fallback and for any deep-linked test that still targets it).
  const allowed = group ? tabsForGroup(group) : null;
  const isVisible = (key) => (allowed ? allowed.has(key) : true);

  // iter-scope-visibility — Per-scope tab restriction. `pro_writer`
  // returns visible=null (grand-fathered full view); financials/coach/
  // editor return a specific Set + ordered list for the sidebar.
  const scopeInfo = useMemo(() => tabsForScopes(scopes), [scopes]);
  const showTab = (key) => {
    if (allowed && !allowed.has(key)) return false;
    if (scopeInfo.visible !== null && !scopeInfo.visible.has(key)) return false;
    return true;
  };

  // iter-sidebar-search — Live filter for the (very long) tab list.
  // Uses a DOM-level toggle on every child of the scroll container so
  // we don't have to touch each of the 40+ TabsTriggers individually.
  const [tabFilter, setTabFilter] = useState('');

  // iter-admin-drawer-all-devices — the panel-list sidebar is now a
  // slide-over drawer on every screen size, not just mobile, so the
  // active panel always gets the full content width by default. It
  // closes itself once a tab is picked.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // iter-sidebar-favorites — Per-admin starred tabs float to the top
  // of the (filtered) list. Persisted in localStorage keyed by admin
  // username; falls back to the anonymous slot before the identity
  // has resolved. Stored as an ORDERED ARRAY so drag-to-reorder
  // (iter-favs-drag) reflects the operator's preferred priority
  // instead of insertion order.
  const FAV_STORAGE_KEY = `cnr:admin-tab-favs:${adminAuth?.user || '_'}`;
  const [favOrder, setFavOrder] = useState(() => {
    try {
      const raw = window.localStorage.getItem(FAV_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        // Backwards-compat: previously stored as an array from a Set —
        // still an array, so this branch handles both shapes.
        if (Array.isArray(parsed)) return parsed.filter(x => typeof x === 'string');
      }
    } catch { /* private mode */ }
    return [];
  });
  const favOrderRef = useRef(favOrder);
  useEffect(() => { favOrderRef.current = favOrder; }, [favOrder]);
  const persistFavs = (arr) => {
    try { window.localStorage.setItem(FAV_STORAGE_KEY, JSON.stringify(arr)); }
    catch { /* private mode */ }
  };
  const toggleFavorite = (value) => {
    setFavOrder((prev) => {
      const next = prev.includes(value)
        ? prev.filter(v => v !== value)
        : [...prev, value];
      persistFavs(next);
      return next;
    });
  };
  const reorderFavorite = (fromValue, toValue) => {
    setFavOrder((prev) => {
      const from = prev.indexOf(fromValue);
      const to   = prev.indexOf(toValue);
      if (from < 0 || to < 0 || from === to) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      persistFavs(next);
      return next;
    });
  };
  const toggleFavRef  = useRef(toggleFavorite);
  const reorderFavRef = useRef(reorderFavorite);
  useEffect(() => { toggleFavRef.current  = toggleFavorite;  });
  useEffect(() => { reorderFavRef.current = reorderFavorite; });

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const q = tabFilter.trim().toLowerCase();
    // Defer so React finishes rendering the current tab set first.
    const raf = window.requestAnimationFrame(() => {
      const scroll = document.querySelector('[data-testid="admin-sidebar-scroll"]');
      if (!scroll) return;
      const nodes = scroll.querySelectorAll('[data-testid^="admin-tab-"]');
      nodes.forEach((n) => {
        const text  = (n.textContent || '').toLowerCase();
        const value = n.getAttribute('data-testid').replace(/^admin-tab-/, '');
        const favIdx = favOrderRef.current.indexOf(value);
        const isFav  = favIdx >= 0;
        n.style.display = (!q || text.includes(q)) ? '' : 'none';
        // iter-favs-drag — Order by array position: first fav = order
        // -1000, second = -999, ... so priorities are preserved and
        // non-fav triggers stay at 0.
        n.style.order = isFav ? String(-1000 + favIdx) : '0';
        n.dataset.favValue = value;

        // Star toggle — injected once.
        let star = n.querySelector('[data-tab-star]');
        if (!star) {
          star = document.createElement('span');
          star.setAttribute('data-tab-star', '1');
          star.setAttribute('data-testid', `admin-tab-fav-${value}`);
          star.setAttribute('role', 'button');
          star.setAttribute('aria-label', 'Toggle favourite');
          star.setAttribute('title', 'Star to pin this tab to the top · drag to reorder starred tabs');
          star.style.marginLeft = 'auto';
          star.style.fontSize = '10px';
          star.style.lineHeight = '1';
          star.style.padding = '2px 4px';
          star.style.cursor = 'pointer';
          star.style.transition = 'opacity 120ms, color 120ms';
          star.textContent = '★';
          star.addEventListener('click', (e) => {
            e.stopPropagation();
            e.preventDefault();
            toggleFavRef.current(n.dataset.favValue);
          });
          n.appendChild(star);
        }
        star.style.color   = isFav ? 'hsl(28 90% 45%)' : 'currentColor';
        star.style.opacity = isFav ? '1' : '0.35';

        // iter-favs-drag — bind drag handlers exactly once per tab.
        // Handlers early-return based on the ref so unstarring a tab
        // silently makes drag a no-op.
        if (!n.dataset.dragBound) {
          n.dataset.dragBound = '1';
          n.addEventListener('dragstart', (e) => {
            const v = n.dataset.favValue;
            if (!favOrderRef.current.includes(v)) { e.preventDefault(); return; }
            e.dataTransfer.setData('text/plain', v);
            e.dataTransfer.effectAllowed = 'move';
            n.style.opacity = '0.5';
          });
          n.addEventListener('dragend', () => { n.style.opacity = ''; });
          n.addEventListener('dragover', (e) => {
            const v = n.dataset.favValue;
            if (!favOrderRef.current.includes(v)) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            n.style.outline = '2px dashed hsl(28 90% 45%)';
          });
          n.addEventListener('dragleave', () => { n.style.outline = ''; });
          n.addEventListener('drop', (e) => {
            n.style.outline = '';
            const from = e.dataTransfer.getData('text/plain');
            const to   = n.dataset.favValue;
            if (!from || !to || from === to) return;
            e.preventDefault();
            reorderFavRef.current(from, to);
          });
          // iter-favs-keyboard — Alt+↑/↓ on a starred tab shifts it up
          // or down the favorites list without touching the mouse.
          n.addEventListener('keydown', (e) => {
            if (!e.altKey) return;
            if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
            const v = n.dataset.favValue;
            const arr = favOrderRef.current;
            const idx = arr.indexOf(v);
            if (idx < 0) return;
            const target = e.key === 'ArrowUp' ? idx - 1 : idx + 1;
            if (target < 0 || target >= arr.length) return;
            e.preventDefault();
            e.stopPropagation();
            reorderFavRef.current(v, arr[target]);
            // iter-favs-announce — audible feedback so keyboard users
            // don't have to scan the sidebar for confirmation. The new
            // position is `target+1` because reorder places the moved
            // entry AT the target slot.
            const label = (n.textContent || v).replace('★', '').trim().slice(0, 40);
            toast.success(`${label} → position ${target + 1} of ${arr.length}`, { duration: 1600 });
          });
        }
        // Draggable ONLY when starred so non-favs behave normally.
        n.draggable = isFav;
      });
    });
    return () => window.cancelAnimationFrame(raf);
  }, [tabFilter, tab, scopes, favOrder]);

  // iter-inbox-badge — Poll unread count so the Administrator Inbox
  // tab shows a red dot without opening the panel.
  const [inboxUnread, setInboxUnread] = useState(0);
  useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    const pull = () => adminInboxApi.stats()
      .then((r) => { if (!cancelled) setInboxUnread(Math.max(0, parseInt(r?.unread || 0, 10) || 0)); })
      .catch(() => { /* silent — badge just won't render */ });
    pull();
    const id = setInterval(pull, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [authed]);

  useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    adminApi.me().then(r => {
      if (cancelled) return;
      setScopes(r?.scopes || []);
    }).catch(() => { if (!cancelled) setScopes([]); });
    return () => { cancelled = true; };
  }, [authed]);

  // iter-51z — bounce a bookstore-only admin to /bookstore/admin.
  // iter55q — but NOT if they also have coach/editor role — they need
  // to reach /admin to see the Human Services tab.
  useEffect(() => {
    if (!authed || scopes === null) return;
    const hasHs = scopes.includes('coach') || scopes.includes('editor');
    if (!scopes.includes('pro_writer') && !hasHs && scopes.includes('bookstore')) {
      toast.info('This account is bookstore-only — redirecting to the Bookstore admin.');
      // Defer so the toast can render before the navigation tears down the
      // component tree.
      setTimeout(() => { window.location.href = '/bookstore/admin'; }, 800);
    }
  }, [authed, scopes]);

  // iter55q — coach/editor without pro_writer should land on the human
  // services tab automatically, since it's the only tab they'll see.
  useEffect(() => {
    if (!authed || scopes === null) return;
    const hasHs = scopes.includes('coach') || scopes.includes('editor');
    if (hasHs && !scopes.includes('pro_writer')) {
      setTab('editorial-review');
    }
  }, [authed, scopes]);

  // iter-bookkeeper-tour · Sep-2026 — a `ledger_entry`-only admin (no
  // `pro_writer`/`financials`) lands straight on the focused
  // Bookkeeping panel, same pattern as the coach/editor redirect
  // above. Without this they'd default to the `users` tab, which
  // isn't even in their visible set.
  useEffect(() => {
    if (!authed || scopes === null) return;
    const financeOnly = scopes.includes('ledger_entry')
      && !scopes.includes('pro_writer') && !scopes.includes('financials');
    if (financeOnly) setTab('bookkeeping');
  }, [authed, scopes]);

  // iter-admin-themes — Apply the admin's preferred palette as soon as
  // the page mounts and again after login (per-admin storage key).
  useEffect(() => { applyAdminTheme(readAdminTheme()); }, [authed]);

  useEffect(() => {
    (async () => {
      // Cookie-based auth: probe /admin/verify only when there's a hint that
      // a cookie might be present (HttpOnly cookies aren't readable from JS,
      // so we look for the persisted username marker the login flow drops in
      // localStorage). This stops the no-op 401 on every fresh page load.
      if (typeof window !== 'undefined' && !adminAuth.user) {
        return;
      }
      try {
        await adminApi.verify();
        setAuthed(true);
      } catch {
        adminAuth.clear();
      }
    })();
    // iter-32 — session hardening. Two things together stop the
    // "VPN flapping logs me out" pain:
    //   (a) ping the heartbeat every 4 minutes while the tab is visible,
    //       which slides the idle window forward server-side regardless
    //       of whether the user is actively clicking.
    //   (b) the 401 listener (admin:session-expired) only fires when the
    //       *server* has rejected the cookie — VPN hops alone won't trigger
    //       it because we never IP-bind.
    const onExpired = (ev) => {
      if (!authedRef.current) return;
      const reason = ev?.detail?.reason || 'expired';
      // iter55z-c fix — Discriminate "browser rejected the cookie"
      // (typical of cross-origin production deploys where SameSite=Lax
      // blocks the session cookie from being sent on XHR) from a real
      // idle timeout. The old code always blamed idle timeout even
      // seconds after a successful login, which was actively misleading.
      if (reason === 'cookie-missing') {
        toast.error(
          'Login succeeded but the browser rejected the session cookie.',
          {
            description: 'This usually means the API is on a different site than the frontend. Ask an operator to set COOKIE_SAMESITE=none in the server env and redeploy.',
            duration: 12000,
          },
        );
      } else {
        toast.info('Your admin session timed out (60 min idle). Please sign in again.');
      }
      setAuthed(false);
      adminAuth.clear();
    };
    window.addEventListener('admin:session-expired', onExpired);

    let pingId = null;
    const ping = () => {
      if (document.hidden || !authedRef.current) return;
      adminApi.heartbeat().catch(() => { /* the 401 listener handles real expiry */ });
    };
    pingId = window.setInterval(ping, 4 * 60 * 1000);
    document.addEventListener('visibilitychange', ping);

    return () => {
      window.removeEventListener('admin:session-expired', onExpired);
      document.removeEventListener('visibilitychange', ping);
      if (pingId) window.clearInterval(pingId);
    };
  }, []);

  // Track latest `authed` for the event listener without re-binding it.
  const authedRef = React.useRef(false);
  authedRef.current = authed;

  // iter-greyrom — emailed reset-link flow (mirrors AdminHubPage).
  // Backend routes the link to `admin_recovery_email` from app_config
  // (defaults to admin@crownnrose.com). Response is 200 whether or
  // not the address is configured, so no enumeration is possible.
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const sendResetLink = async () => {
    if (forgotBusy) return;
    setForgotBusy(true);
    try {
      const adminUrl = `${window.location.origin}/admin/reset-password`;
      await adminApi.requestPasswordReset(adminUrl);
      setForgotSent(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send reset link.');
    } finally { setForgotBusy(false); }
  };

  const login = async () => {
    setBusy(true);
    try {
      const r = await adminApi.login(pwd, user);
      // iter55z-c fix — Immediately probe /admin/me. If the browser
      // didn't accept the session cookie (cross-origin SameSite=Lax
      // in production is the usual culprit), this call 401s within
      // milliseconds and we can render the correct diagnosis instead
      // of a misleading "idle timeout" toast that fires 4 minutes
      // later on the first heartbeat.
      let cookieOk = true;
      try {
        await adminApi.me();
      } catch (probe) {
        if (probe?.response?.status === 401) cookieOk = false;
      }
      if (!cookieOk) {
        toast.error('Login accepted but the browser refused the session cookie.', {
          description: 'The API and frontend are on different sites. An operator must set COOKIE_SAMESITE=none in the server env and redeploy.',
          duration: 15000,
        });
        setPwd('');
        return;
      }
      // Persist the username marker so the mount-time verify() probe
      // knows to fire on future visits.
      adminAuth.user = r.user || user;
      setAuthed(true); setPwd('');
      window.dispatchEvent(new Event('admin:authenticated'));
      toast.success(`Welcome, ${r.user || user}.`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Login failed.'); }
    finally { setBusy(false); }
  };

  const logout = async () => {
    await adminApi.logout();
    setAuthed(false);
    window.dispatchEvent(new Event('admin:signed-out'));
    // iter-admin-themes — drop the theme attribute on logout so the
    // writing suite / bookstore re-inherit their default parchment.
    if (typeof document !== 'undefined') {
      document.documentElement.removeAttribute('data-admin-theme');
    }
  };

  if (!authed) {
    if (gateChecking) {
      return <div className="min-h-screen w-full bg-background" data-testid="admin-gate-checking" />;
    }
    if (gateNeeded) {
      return <AdminMobileGate onPassed={() => setGateNeeded(false)} />;
    }
    return (
      <div className="min-h-screen w-full bg-background flex items-center justify-center p-6">
        <div className="w-full max-w-sm rounded-md border border-border bg-card p-6 shadow-panel">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground shadow-inset-top"><ShieldCheck className="h-4 w-4" /></div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">The Loom V2.38_f55</div>
              <div className="text-[15px] font-semibold">Admin Console</div>
            </div>
          </div>
          <p className="mt-4 text-[12px] text-muted-foreground">Sign in as <b>Administrator</b> or <b>David</b> to manage users, pricing, AI models, API keys, and database collections.</p>
          <div className="mt-4 space-y-2">
            <Label className="text-[11.5px]">User</Label>
            <select
              data-testid="admin-user-select"
              value={user}
              onChange={(e) => setUser(e.target.value)}
              className="h-9 w-full rounded-sm border border-border bg-background px-2 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="Administrator">Administrator</option>
              <option value="David">David</option>
            </select>
          </div>
          <div className="mt-3 space-y-2">
            <Label className="text-[11.5px]">Password</Label>
            <Input data-testid="admin-password-input" type="password" autoFocus value={pwd} onChange={(e) => setPwd(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && login()} placeholder="••••••••" />
          </div>
          <Button data-testid="admin-login-btn" onClick={login} disabled={busy || !pwd} className="mt-4 w-full gap-1.5">
            <Lock className="h-3.5 w-3.5" /> {busy ? 'Signing in…' : 'Sign in'}
          </Button>
          {/* iter-greyrom — Forgot password? on every admin sign-in dialog. */}
          {forgotSent ? (
            <p className="mt-2 rounded-sm border border-emerald-400/50 bg-emerald-50/60 px-2 py-1.5 text-center text-[11px] text-emerald-900" data-testid="admin-login-reset-sent">
              If a recovery email is configured, a reset link is on its way. It expires in 60 minutes.
            </p>
          ) : (
            <button
              type="button"
              onClick={sendResetLink}
              disabled={forgotBusy}
              data-testid="admin-login-forgot"
              className="mt-2 w-full text-center text-[11px] text-muted-foreground hover:text-primary hover:underline disabled:opacity-50"
            >
              {forgotBusy ? 'Sending…' : 'Forgot password? Email me a reset link'}
            </button>
          )}
          <p className="mt-3 rounded-sm border border-border bg-panel-header/30 px-2 py-1.5 text-[10.5px] text-muted-foreground" data-testid="admin-login-security-note">
            Admin access uses individually hashed credentials. Use the email reset flow if your password is unavailable.
          </p>
          <div className="mt-4 flex items-center justify-between text-[11px] text-muted-foreground">
            <Link to="/" className="hover:text-primary inline-flex items-center gap-1"><BookOpen className="h-3 w-3" /> Back to app</Link>
            <span className="font-mono-stat">/api/admin</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col bg-background" data-testid="admin-desktop-workspace">
      <AdminSessionWarning onRelogin={logout} />
      <GuidedTour />
      <div className="flex items-center justify-between gap-2 border-b border-border bg-panel-header/40 px-2 py-2 sm:px-4">
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-sm bg-primary text-primary-foreground"><ShieldCheck className="h-3.5 w-3.5" /></div>
          <div className="min-w-0">
            <div className="hidden text-[10.5px] uppercase tracking-wider text-muted-foreground sm:block">The Loom V2.38_f55</div>
            <div className="truncate text-[13px] font-semibold leading-tight">
              Admin Console{group ? ` · ${GROUP_META[group]?.label || group}` : ''}
            </div>
          </div>
          {/* iter55f73 — Group navigator (Finance / Users / Operations).
              Hidden below `lg` — it lives inside the drawer instead (see
              SheetContent below), since it didn't fit the mobile header
              and was effectively invisible there. */}
          {(
            <nav className="ml-4 hidden items-center gap-1 lg:flex" data-testid="admin-group-nav-desktop">
              <Link
                to="/admin"
                data-testid="admin-group-hub-desktop"
                className="inline-flex items-center gap-1 rounded-sm border border-transparent px-2 py-1 text-[11px] font-semibold text-muted-foreground hover:border-border hover:text-foreground"
              >
                <LayoutGrid className="h-3 w-3" /> Hub
              </Link>
              {['finance', 'users', 'operations', 'editing', 'marketing', 'it'].map((g) => {
                const GIcon = GROUP_ICONS[g];
                return (
                  <Link
                    key={g}
                    to={GROUP_META[g].path}
                    data-testid={`admin-group-${g}-desktop`}
                    className="inline-flex items-center gap-1 rounded-sm border px-2 py-1 text-[11px] font-semibold"
                    style={{
                      backgroundColor: g === group ? 'hsl(var(--primary) / 0.14)' : 'transparent',
                      color:           g === group ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))',
                      borderColor:     g === group ? 'hsl(var(--primary) / 0.4)' : 'transparent',
                    }}
                  >
                    <GIcon className="h-3 w-3" /> {GROUP_META[g].label}
                  </Link>
                );
              })}
            </nav>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1 overflow-x-auto">
          <PanelEmailButton tabValue={tab} callerScopes={scopes || []} />
          {/* iter-dev-mailbox — dedicated dev@crownnrose.com mailbox button
              next to the scope-aware Panel Inbox. Only visible to admins with
              the `dev` or `pro_writer` scope. Hidden below `sm` — secondary
              action, and the main drawer trigger needs the room more. */}
          <div className="hidden sm:block">
            <PanelEmailButton
              tabValue={tab}
              callerScopes={scopes || []}
              fixedScope="dev"
              label="dev@"
              testIdPrefix="admin-dev-mailbox"
            />
          </div>
          <AdminHelpButton env="pro_writer" currentPanelId={tab} />
          {/* Sep-2026 — Ledger Clerk approval nudge lives here too, not
              just the Admin Hub, since ledger_entry-only admins land
              straight on this Finance surface and never see the Hub. */}
          <AdminNotificationsBell adminToken={adminAuth.token} />
          {/* ── Admin Mailbox — system-orange quick-access button ──────────
               Sits next to the Notifications bell on every panel so admins
               can reach the inbox without scrolling the sidebar. Badge mirrors
               the same unread count polled above. */}
          <button
            type="button"
            data-testid="admin-mailbox-topbar-btn"
            title={`Administrator Inbox${inboxUnread > 0 ? ` · ${inboxUnread} unread` : ''}`}
            onClick={() => { setTab('admin-inbox'); setMobileNavOpen(false); }}
            className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-sm transition-all hover:opacity-90 active:scale-95"
            style={{ backgroundColor: 'hsl(28 90% 45%)', color: '#fff' }}
          >
            <Mail className="h-3.5 w-3.5" />
            {inboxUnread > 0 && (
              <span
                className="absolute -right-0.5 -top-0.5 flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-rose-600 px-0.5 text-[8px] font-bold leading-none text-white"
                data-testid="admin-mailbox-topbar-badge"
                aria-label={`${inboxUnread} unread`}
              >
                {inboxUnread > 9 ? '9+' : inboxUnread}
              </span>
            )}
          </button>
          {/* 2026-08 — Editor/admin Loom bridge manuscript picker. Lives
              in this shared header (not just the Hub) so editor-scope
              staff — who never land on the Hub — can still reach it. */}
          {(scopes === null || scopes.includes('pro_writer') || scopes.includes('editor')) && (
            <AdminLoomManuscriptPicker />
          )}
          <Link to="/" className="hidden sm:inline-flex"><Button variant="ghost" size="sm" className="h-7 gap-1.5 text-[12px]"><BookOpen className="h-3.5 w-3.5" /> App</Button></Link>
          {/* Cache status dot — same indicator as the writer StatusBar */}
          <span className="hidden sm:flex items-center gap-1 px-1" title="Anthropic prompt-cache status — orange: waiting for first AI call; green: cache hit; red: cache write">
            <CacheStatusDot />
            <span className="text-[10px] text-amber-500/80">Cache</span>
          </span>
          <Button variant="ghost" size="sm" onClick={logout} className="h-7 w-7 shrink-0 gap-1.5 p-0 text-[12px] text-destructive sm:w-auto sm:px-2" data-testid="admin-logout-btn"><LogOut className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Sign out</span></Button>
        </div>
      </div>

      {/* iter55z-k — Live/test mode banner. Renders directly under the
          admin chrome so it's visible on every panel. */}
      <StripeModeBanner />

      {/* Feb-2026 · ExecSummary moved to a compact square inside the
          Financial Statements right column. No longer renders full-width
          above the tab strip on any group. */}

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setMobileNavOpen(false); }} className="flex flex-1 min-h-0">
        {/* ── Left-edge panel activator ─────────────────────────────────────
            Replaces the top-bar hamburger. A slim vertical handle pinned to
            the left side of the content area. Signals there are more panels
            in the drawer via the count badge + label hint. */}
        <button
          type="button"
          onClick={() => setMobileNavOpen(true)}
          data-testid="admin-panels-left-trigger"
          title={`Open admin panels${allowed ? ` · ${allowed.size} panels` : ''}`}
          className="group flex w-7 shrink-0 flex-col items-center justify-center gap-2 self-center border border-l-0 border-border/70 bg-primary/8 py-5 hover:bg-primary/15 transition-colors"
          style={{
            height: '108px',
            borderRadius: '0 6px 6px 0',
            borderLeft: 'none',
            boxShadow: '2px 0 10px -4px hsl(var(--primary) / 0.25)',
          }}
        >
          <Menu className="h-3.5 w-3.5 text-primary/70 group-hover:text-primary transition-colors" />
          <span className="select-none [writing-mode:vertical-lr] rotate-180 text-[8.5px] uppercase tracking-[0.18em] text-primary/60 group-hover:text-primary transition-colors">
            Panels
          </span>
          {allowed && (
            <span className="inline-flex items-center justify-center rounded-full bg-primary/15 px-1 py-0.5 text-[9px] font-bold text-primary min-w-[16px] group-hover:bg-primary/30">
              {allowed.size}
            </span>
          )}
        </button>
        {(() => { const sidebarList = (
        <TabsList
          className="flex flex-1 min-h-0 w-full flex-col items-stretch justify-start gap-0 overflow-visible rounded-none border-0 bg-transparent p-2"
          data-testid="admin-sidebar-scroll-area"
        >
          {(() => {
            const scopeOrder = scopeInfo.order;   // iter-scope-visibility — restricted admins get a curated order
            return (
              <>
                {/* iter-pin-pricing — Pricing pinned so the Payment
                    Processing master switch is always one click away
                    regardless of sidebar scroll position. */}
                {showTab('pricing') && (scopes === null || scopes.includes('pro_writer')) && (
                  <div className="mb-1 shrink-0" data-testid="admin-sidebar-pinned-top">
                    <TabsTrigger
                      value="pricing"
                      className="w-full justify-start gap-2 h-8 text-[12px] rounded-sm border border-primary/40 bg-primary/5 data-[state=active]:bg-background data-[state=active]:border-primary"
                      data-testid="admin-tab-pricing"
                    >
                      <DollarSign className="h-3.5 w-3.5" /> Pricing
                    </TabsTrigger>
                    <div className="mx-1 my-1 border-t border-border" />
                  </div>
                )}

                {/* iter-sidebar-search — Filter box above the scroll. */}
                <div className="mb-1 shrink-0" data-testid="admin-sidebar-search-wrap">
                  <div className="relative">
                    <SearchIcon className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="text"
                      value={tabFilter}
                      onChange={(e) => setTabFilter(e.target.value)}
                      placeholder="Filter tabs…"
                      className="h-7 w-full rounded-sm border border-border bg-background pl-6 pr-2 text-[11.5px] placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary"
                      data-testid="admin-sidebar-search-input"
                    />
                    {tabFilter && (
                      <button
                        type="button"
                        onClick={() => setTabFilter('')}
                        aria-label="Clear tab filter"
                        data-testid="admin-sidebar-search-clear"
                        className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>

                {/* Keep the panel appearance menu outside the scroll container.
                    It now opens downward over the list instead of being clipped
                    against the bottom edge of the sidebar. */}
                <div className="relative z-20 mb-2 shrink-0 border-b border-border pb-2">
                  <AdminThemeSwitcher />
                </div>

                {/* Scrollable panel list now owns all space between search
                    and the sidebar edge; Admin tools live in the drawer. */}
                <div
                  className="relative flex min-h-0 flex-1 basis-0 flex-col overflow-y-auto overflow-x-visible scroll-thin -mr-1 pr-1 pb-3"
                  data-testid="admin-sidebar-scroll"
                >
                {showTab('users')       && <TabsTrigger value="users"       className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-users"><Users className="h-3.5 w-3.5" /> Users &amp; Tokens</TabsTrigger>}
                {showTab('archived-users') && <TabsTrigger value="archived-users" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-archived-users"><Archive className="h-3.5 w-3.5" /> Archived Users</TabsTrigger>}
                {showTab('teams')       && <TabsTrigger value="teams"       className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-teams"><UserPlus className="h-3.5 w-3.5" /> Teams &amp; Projects</TabsTrigger>}
                {showTab('roles')       && <TabsTrigger value="roles"       className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-roles"><Shield className="h-3.5 w-3.5" /> Roles</TabsTrigger>}
                {showTab('memberships') && <TabsTrigger value="memberships" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-memberships"><UserPlus className="h-3.5 w-3.5" /> Project Members</TabsTrigger>}
                {showTab('budget')      && <TabsTrigger value="budget"      className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-budget"><Wallet className="h-3.5 w-3.5" /> AI Budget</TabsTrigger>}
                {showTab('models')      && <TabsTrigger value="models"      className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-models"><Cpu className="h-3.5 w-3.5" /> AI Models</TabsTrigger>}
                {showTab('loom')        && <TabsTrigger value="loom"        className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-loom"><Settings className="h-3.5 w-3.5" /> Loom Settings</TabsTrigger>}
                {showTab('covers')      && <TabsTrigger value="covers"      className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-covers"><Image className="h-3.5 w-3.5" /> Cover Art</TabsTrigger>}
                {showTab('retro-review') && <TabsTrigger value="retro-review" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-retro-review"><PlayCircle className="h-3.5 w-3.5" /> Retroactive Review</TabsTrigger>}
                {showTab('continuity-review') && <TabsTrigger value="continuity-review" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-continuity-review"><Compass className="h-3.5 w-3.5" /> Continuity &amp; Drift</TabsTrigger>}
                {showTab('plot-builder') && <TabsTrigger value="plot-builder" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-plot-builder"><GitBranch className="h-3.5 w-3.5" /> Plot Builder</TabsTrigger>}
                {showTab('bulk-novel-eval') && <TabsTrigger value="bulk-novel-eval" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-bulk-novel-eval"><Layers className="h-3.5 w-3.5" /> Bulk Eval</TabsTrigger>}
                {showTab('keys')        && <TabsTrigger value="keys"        className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-keys"><KeyRound className="h-3.5 w-3.5" /> API Keys</TabsTrigger>}
                {showTab('vault')       && <TabsTrigger value="vault"       className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-vault"><ShieldCheck className="h-3.5 w-3.5" /> Secrets Vault</TabsTrigger>}
                {showTab('collections') && <TabsTrigger value="collections" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-collections"><Database className="h-3.5 w-3.5" /> Database</TabsTrigger>}
                {showTab('seo')         && <TabsTrigger value="seo"         className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-seo"><Globe className="h-3.5 w-3.5" /> SEO &amp; Bots</TabsTrigger>}
                {showTab('blog')        && <TabsTrigger value="blog"        className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-blog"><Newspaper className="h-3.5 w-3.5" /> Blog</TabsTrigger>}
                {showTab('request-log') && <TabsTrigger value="request-log" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-request-log"><FileText className="h-3.5 w-3.5" /> Request Log</TabsTrigger>}
                {showTab('writer-feedback') && <TabsTrigger value="writer-feedback" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-writer-feedback"><MessageSquare className="h-3.5 w-3.5" /> Writer Feedback</TabsTrigger>}
                {showTab('email-log') && <TabsTrigger value="email-log" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-email-log"><Mail className="h-3.5 w-3.5" /> Email Log</TabsTrigger>}
                {showTab('landing')     && <TabsTrigger value="landing"     className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-landing"><Layout className="h-3.5 w-3.5" /> Landing Page</TabsTrigger>}
                {showTab('what-we-offer') && <TabsTrigger value="what-we-offer" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-what-we-offer"><Layers className="h-3.5 w-3.5" /> What We Offer</TabsTrigger>}
                {showTab('starter-templates') && <TabsTrigger value="starter-templates" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-starter-templates"><Layout className="h-3.5 w-3.5" /> Starter Templates</TabsTrigger>}
                {showTab('genre-variants') && <TabsTrigger value="genre-variants" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-genre-variants"><Skull className="h-3.5 w-3.5" /> Genre Variants</TabsTrigger>}
                {showTab('author-rankings') && <TabsTrigger value="author-rankings" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-author-rankings"><Skull className="h-3.5 w-3.5" /> Author Rankings</TabsTrigger>}
                {showTab('clarity')    && <TabsTrigger value="clarity"    className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-clarity"><TrendingUp className="h-3.5 w-3.5" /> Marketing (Clarity)</TabsTrigger>}
                {showTab('analytics')  && <TabsTrigger value="analytics"  className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-analytics"><TrendingUp className="h-3.5 w-3.5" /> Product Analytics</TabsTrigger>}
                {showTab('human-services') && <TabsTrigger value="human-services" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-human-services"><Users className="h-3.5 w-3.5" /> Human Services</TabsTrigger>}
                {showTab('editorial-review') && <TabsTrigger value="editorial-review" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-editorial-review"><ClipboardCheck className="h-3.5 w-3.5" /> Manuscript Review</TabsTrigger>}
                {showTab('editor-workbench') && <TabsTrigger value="editor-workbench" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-editor-workbench"><Pencil className="h-3.5 w-3.5" /> Editor Workbench</TabsTrigger>}
                {showTab('editing')     && <TabsTrigger value="editing"     className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-editing"><Pencil className="h-3.5 w-3.5" /> Editing</TabsTrigger>}
                {showTab('royalties')   && <TabsTrigger value="royalties"   className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-royalties"><Coins className="h-3.5 w-3.5" /> Royalties</TabsTrigger>}
                {showTab('marketing-bookings') && <TabsTrigger value="marketing-bookings" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-marketing-bookings"><TrendingUp className="h-3.5 w-3.5" /> Marketing Bookings</TabsTrigger>}
                {showTab('isbn')        && <TabsTrigger value="isbn"        className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-isbn"><Hash className="h-3.5 w-3.5" /> ISBN Manager</TabsTrigger>}
                {showTab('audiobook-voices') && <TabsTrigger value="audiobook-voices" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-audiobook-voices"><Headphones className="h-3.5 w-3.5" /> Audiobook Voices</TabsTrigger>}
                {showTab('elevenlabs-narration') && <TabsTrigger value="elevenlabs-narration" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-elevenlabs-narration"><Mic className="h-3.5 w-3.5" /> EL Narrator Studio</TabsTrigger>}
                {showTab('manuscript-inventory') && <TabsTrigger value="manuscript-inventory" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-manuscript-inventory"><BookOpen className="h-3.5 w-3.5" /> Manuscript Inventory</TabsTrigger>}
                {showTab('ms-export') && <TabsTrigger value="ms-export" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-ms-export"><Archive className="h-3.5 w-3.5" /> Manuscript Export</TabsTrigger>}
                {showTab('tax-center')  && <TabsTrigger value="tax-center"  className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-tax-center"><DollarSign className="h-3.5 w-3.5" /> Tax Center</TabsTrigger>}
                {showTab('ohas')        && <TabsTrigger value="ohas"        className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-ohas"><Shield className="h-3.5 w-3.5" /> OHAS Certification</TabsTrigger>}
                {showTab('wipo-contracts') && <TabsTrigger value="wipo-contracts" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-wipo-contracts"><Pencil className="h-3.5 w-3.5" /> WIPO Contracts</TabsTrigger>}
                {showTab('author-agreements') && <TabsTrigger value="author-agreements" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-author-agreements"><Pencil className="h-3.5 w-3.5" /> Publishing Agreements</TabsTrigger>}
                {showTab('isbn-bank') && <TabsTrigger value="isbn-bank" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-isbn-bank"><Pencil className="h-3.5 w-3.5" /> ISBN Bank</TabsTrigger>}
                {showTab('eco-filing') && <TabsTrigger value="eco-filing" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-eco-filing"><Hash className="h-3.5 w-3.5" /> eCO Filing</TabsTrigger>}
                {showTab('admin-users') && <TabsTrigger value="admin-users" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-admin-users"><Shield className="h-3.5 w-3.5" /> Admin Users</TabsTrigger>}
                {showTab('permissions') && <TabsTrigger value="permissions" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-permissions"><KeyRound className="h-3.5 w-3.5" /> Permissions</TabsTrigger>}
                {showTab('admin-overview') && <TabsTrigger value="admin-overview" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-admin-overview"><ShieldCheck className="h-3.5 w-3.5" /> Admin Overview</TabsTrigger>}
                {showTab('bookkeeping') && <TabsTrigger value="bookkeeping" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-bookkeeping"><Landmark className="h-3.5 w-3.5" /> Bookkeeping</TabsTrigger>}
                {showTab('ledger') && <TabsTrigger value="ledger" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-ledger"><BookOpen className="h-3.5 w-3.5" /> Ledger &amp; COA</TabsTrigger>}
                {showTab('balance-sheet') && <TabsTrigger value="balance-sheet" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-balance-sheet"><Scale className="h-3.5 w-3.5" /> Balance Sheet</TabsTrigger>}
                {showTab('income-statement') && <TabsTrigger value="income-statement" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-income-statement"><TrendingUp className="h-3.5 w-3.5" /> Income Statement</TabsTrigger>}
                {showTab('cash-flow') && <TabsTrigger value="cash-flow" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-cash-flow"><ArrowRightLeft className="h-3.5 w-3.5" /> Cash Flow</TabsTrigger>}
                {showTab('equity-statement') && <TabsTrigger value="equity-statement" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-equity-statement"><Coins className="h-3.5 w-3.5" /> Statement of Equity</TabsTrigger>}
                {showTab('compliance') && <TabsTrigger value="compliance" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-compliance"><ShieldCheck className="h-3.5 w-3.5" /> Compliance &amp; Licenses</TabsTrigger>}
                {showTab('aging') && <TabsTrigger value="aging" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-aging"><ArrowDownRight className="h-3.5 w-3.5" /> AP / AR &amp; Dunning</TabsTrigger>}
                {showTab('bank-recon') && <TabsTrigger value="bank-recon" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-bank-recon"><Landmark className="h-3.5 w-3.5" /> Bank Recon</TabsTrigger>}
                {showTab('auto-ledger') && <TabsTrigger value="auto-ledger" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-auto-ledger"><Activity className="h-3.5 w-3.5" /> Auto-Ledger</TabsTrigger>}
                {showTab('ap-approvals') && <TabsTrigger value="ap-approvals" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-ap-approvals"><Scale className="h-3.5 w-3.5" /> AP Approval Queue</TabsTrigger>}
                {showTab('stripe-payments') && <TabsTrigger value="stripe-payments" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-stripe-payments"><DollarSign className="h-3.5 w-3.5" /> Stripe Payments</TabsTrigger>}
                {showTab('inventory') && <TabsTrigger value="inventory" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-inventory"><Package className="h-3.5 w-3.5" /> Inventory</TabsTrigger>}
                {showTab('royalties') && <TabsTrigger value="author-royalty-tiers" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-royalty-tiers"><Shield className="h-3.5 w-3.5" /> Author Royalty Tiers</TabsTrigger>}
                {showTab('royalties') && <TabsTrigger value="broadcasts" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-broadcasts"><Shield className="h-3.5 w-3.5" /> Broadcasts</TabsTrigger>}
                {showTab('help-manual') && <TabsTrigger value="help-manual" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-help-manual"><HelpCircle className="h-3.5 w-3.5" /> Help Manual</TabsTrigger>}
                {showTab('tips')       && <TabsTrigger value="tips"        className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-tips"><HelpCircle className="h-3.5 w-3.5" /> Tip Editor</TabsTrigger>}
                {showTab('revenue-features') && <TabsTrigger value="revenue-features" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-revenue-features"><TrendingUp className="h-3.5 w-3.5" /> Zernio &amp; Revenue</TabsTrigger>}
                {showTab('referrals')        && <TabsTrigger value="referrals"        className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-referrals"><Users className="h-3.5 w-3.5" /> Referrals</TabsTrigger>}
                {showTab('free-tier')        && <TabsTrigger value="free-tier"        className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-free-tier"><ShieldOff className="h-3.5 w-3.5" /> Free Tier</TabsTrigger>}
                {showTab('comparison-pages') && <TabsTrigger value="comparison-pages" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-comparison-pages"><Globe2 className="h-3.5 w-3.5" /> Comparison Pages</TabsTrigger>}
                {showTab('training')   && <TabsTrigger value="training"   className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-training"><PlayCircle className="h-3.5 w-3.5" /> Training Videos</TabsTrigger>}
                {showTab('media-development') && <TabsTrigger value="media-development" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-media-development"><PlayCircle className="h-3.5 w-3.5" /> Media Development</TabsTrigger>}
                {showTab('media-vault') && <TabsTrigger value="media-vault" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-media-vault"><HardDrive className="h-3.5 w-3.5" /> Parent Media Vault</TabsTrigger>}
                {showTab('marketing-venues') && <TabsTrigger value="marketing-venues" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-marketing-venues"><Megaphone className="h-3.5 w-3.5" /> Venue Catalog</TabsTrigger>}
                {showTab('teleplay-qa') && <TabsTrigger value="teleplay-qa" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-teleplay-qa"><Tv className="h-3.5 w-3.5" /> Teleplay QA</TabsTrigger>}
                {showTab('seo-config') && <TabsTrigger value="seo-config" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-seo-config"><Globe className="h-3.5 w-3.5" /> SEO Config</TabsTrigger>}
                {showTab('feature-videos') && <TabsTrigger value="feature-videos" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-feature-videos"><PlayCircle className="h-3.5 w-3.5" /> Feature Videos</TabsTrigger>}
                {showTab('feature-videos') && <TabsTrigger value="training-bundles" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-training-bundles"><PlayCircle className="h-3.5 w-3.5" /> Training Bundles</TabsTrigger>}
                {showTab('demo-funnel') && <TabsTrigger value="demo-funnel" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-demo-funnel"><PlayCircle className="h-3.5 w-3.5" /> Demo Funnel</TabsTrigger>}
                {showTab('audit')       && <TabsTrigger value="audit"       className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-audit"><Activity className="h-3.5 w-3.5" /> Activity</TabsTrigger>}
                {showTab('security')    && <TabsTrigger value="security"    className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-security"><Activity className="h-3.5 w-3.5" /> Security Console</TabsTrigger>}
                {showTab('accounting')  && <TabsTrigger value="accounting"  className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-accounting"><BarChart3 className="h-3.5 w-3.5" /> Accounting</TabsTrigger>}
                {showTab('financials')  && <TabsTrigger value="financials"  className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-financials"><BarChart3 className="h-3.5 w-3.5" /> Financials</TabsTrigger>}
                {showTab('canva-dashboard') && <TabsTrigger value="canva-dashboard" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-canva-dashboard"><BarChart3 className="h-3.5 w-3.5" /> Canva Covers</TabsTrigger>}
                {showTab('plans')       && <TabsTrigger value="plans"       className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-plans"><Crown className="h-3.5 w-3.5" /> Plans</TabsTrigger>}
                {showTab('unit-econ')   && <TabsTrigger value="unit-econ"   className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-unit-econ"><BarChart3 className="h-3.5 w-3.5" /> Unit Economics</TabsTrigger>}
                {showTab('unit-econ')   && <TabsTrigger value="shelf-attribution" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-shelf-attribution"><BarChart3 className="h-3.5 w-3.5" /> Shelf Attribution</TabsTrigger>}
                {showTab('unit-econ')   && <TabsTrigger value="search-visibility" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-search-visibility"><Youtube className="h-3.5 w-3.5" /> Search Visibility</TabsTrigger>}
                {showTab('ai-caching') && <TabsTrigger value="ai-caching" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-ai-caching"><Zap className="h-3.5 w-3.5" /> AI Caching</TabsTrigger>}
                {showTab('capacity')    && <TabsTrigger value="capacity"    className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-capacity"><HardDrive className="h-3.5 w-3.5" /> Capacity</TabsTrigger>}
                {showTab('indie-reader') && <TabsTrigger value="indie-reader" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-indie-reader"><BookOpen className="h-3.5 w-3.5" /> Indie Reader</TabsTrigger>}
                {showTab('reader-review') && <TabsTrigger value="reader-review" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-reader-review"><ShieldCheck className="h-3.5 w-3.5" /> Reader Review Queue</TabsTrigger>}
                {showTab('editor-seats') && <TabsTrigger value="editor-seats" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-editor-seats"><ShieldCheck className="h-3.5 w-3.5" /> Editor Seats</TabsTrigger>}
                {showTab('companion-inbox') && <TabsTrigger value="companion-inbox" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-companion-inbox"><Mail className="h-3.5 w-3.5" /> Companion Inbox</TabsTrigger>}
                {showTab('reader-sync') && <TabsTrigger value="reader-sync" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-reader-sync"><Activity className="h-3.5 w-3.5" /> Reader Sync</TabsTrigger>}
                {showTab('admin-inbox') && (
                  <TabsTrigger
                    value="admin-inbox"
                    className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background"
                    data-testid="admin-tab-admin-inbox"
                  >
                    <Mail className="h-3.5 w-3.5" /> Administrator Inbox
                    {inboxUnread > 0 && (
                      <span
                        className="ml-auto inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-600 px-1 text-[9.5px] font-bold leading-none text-white"
                        data-testid="admin-tab-admin-inbox-badge"
                        aria-label={`${inboxUnread} unread`}
                      >
                        {inboxUnread > 99 ? '99+' : inboxUnread}
                      </span>
                    )}
                  </TabsTrigger>
                )}
                {showTab('email-workers') && <TabsTrigger value="email-workers" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-email-workers"><Globe className="h-3.5 w-3.5" /> Email Workers CLI</TabsTrigger>}
                {showTab('it-management') && <TabsTrigger value="it-management" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-it-management"><ShieldCheck className="h-3.5 w-3.5" /> IT Management</TabsTrigger>}
                <TabsTrigger value="profile"     className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-profile"><UserCog className="h-3.5 w-3.5" /> My Profile</TabsTrigger>
                <TabsTrigger value="admin-guide" className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-admin-guide"><LifeBuoy className="h-3.5 w-3.5" /> Admin Guide</TabsTrigger>
                {showTab('qa')          && <TabsTrigger value="qa"          className="justify-start gap-2 h-8 text-[12px] data-[state=active]:bg-background" data-testid="admin-tab-qa"><FlaskConical className="h-3.5 w-3.5" /> QA &amp; Tests</TabsTrigger>}
                {/* iter-scope-visibility — Empty-state card for restricted admins with no matching tabs. */}
                {scopeInfo.visible !== null && scopeInfo.order.filter(t => !['profile','admin-guide','admin-inbox'].includes(t)).length === 0 && (
                  <div className="mt-2 rounded-md border border-dashed border-border bg-panel-header/20 p-3 text-[11.5px] text-muted-foreground" data-testid="admin-sidebar-empty-state">
                    <div className="font-semibold text-foreground">No panels available</div>
                    <p className="mt-1">Your admin account has no scopes wired to visible panels yet. Ask a full-console admin to grant a scope (e.g. <code>financials</code>, <code>editor</code>, or <code>coach</code>) from <b>Admin → Admin Users</b>.</p>
                    <a
                      href="mailto:admin@crownnrose.com?subject=Admin%20scope%20request&body=Please%20grant%20me%20a%20scope%20so%20I%20can%20reach%20the%20admin%20panels%20for%20my%20role."
                      className="mt-2 inline-flex items-center gap-1 rounded-sm border border-primary/50 bg-primary/10 px-2 py-1 text-[11px] font-semibold text-primary hover:bg-primary/20"
                      data-testid="admin-sidebar-empty-state-support-btn"
                    >
                      <LifeBuoy className="h-3 w-3" /> Open a support ticket
                    </a>
                  </div>
                )}
                </div>
              </>
            );
          })()}
          {/* ── Admin Tools — pinned at sidebar bottom, always visible ─────
               Moved from the top bar (Oct-2026) so it's reachable without
               the header being in focus. Amber indicator signals the drawer
               is populated before it's opened. */}
          <div className="mt-2 shrink-0 border-t border-border pt-2" data-testid="admin-tools-drawer-wrap">
            <AdminToolsDrawer />
          </div>
        </TabsList>
        ); return (
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetContent
              side="left"
              className="w-[82vw] max-w-[300px] flex flex-col p-0 overflow-hidden"
              data-testid="admin-sidebar-drawer"
            >
              <SheetHeader className="border-b border-border px-3 py-2.5">
                <SheetTitle className="text-[13px]">Admin Panels</SheetTitle>
              </SheetHeader>
              {/* iter-admin-drawer-departments — department switcher now
                  lives here too, so every device gets ONE drawer with
                  both "other departments" AND "panels" in it. */}
              {(
                <nav className="flex flex-wrap gap-1.5 border-b border-border px-3 py-2.5" data-testid="admin-group-nav">
                  <Link
                    to="/admin"
                    onClick={() => setMobileNavOpen(false)}
                    data-testid="admin-group-hub"
                    className="inline-flex items-center gap-1 rounded-sm border border-border px-2 py-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground"
                  >
                    <LayoutGrid className="h-3 w-3" /> Hub
                  </Link>
                  {['finance', 'users', 'operations', 'editing', 'marketing', 'it'].map((g) => {
                    const GIcon = GROUP_ICONS[g];
                    return (
                      <Link
                        key={g}
                        to={GROUP_META[g].path}
                        onClick={() => setMobileNavOpen(false)}
                        data-testid={`admin-group-${g}`}
                        className="inline-flex items-center gap-1 rounded-sm border border-border px-2 py-1 text-[11px] font-semibold"
                        style={{
                          backgroundColor: g === group ? 'hsl(var(--primary) / 0.14)' : 'transparent',
                          color:           g === group ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))',
                          borderColor:     g === group ? 'hsl(var(--primary) / 0.4)' : undefined,
                        }}
                      >
                        <GIcon className="h-3 w-3" /> {GROUP_META[g].label}
                      </Link>
                    );
                  })}
                </nav>
              )}
              {sidebarList}
            </SheetContent>
          </Sheet>
        ); })()}

        <div
          className="flex-1 min-w-0 min-h-0 overflow-y-auto scroll-thin p-6 xl:pr-[20rem]"
          data-testid="admin-panel-scroll-area"
        >
          <TabsContent value="users"       className="m-0"><UsersPanel /></TabsContent>
          <TabsContent value="archived-users" className="m-0"><ArchivedUsersPanel /></TabsContent>
          <TabsContent value="teams"       className="m-0"><TeamsPanel /></TabsContent>
          <TabsContent value="roles"       className="m-0"><RolesPanel /></TabsContent>
          <TabsContent value="memberships" className="m-0"><MembershipsPanel /></TabsContent>
          <TabsContent value="pricing"     className="m-0"><PricingPanel /></TabsContent>
          <TabsContent value="budget"      className="m-0"><BudgetPanel /></TabsContent>
          <TabsContent value="models"      className="m-0"><ModelsPanel /></TabsContent>
          <TabsContent value="loom"        className="m-0"><LoomSettingsPanel /></TabsContent>
          <TabsContent value="covers"      className="m-0"><CoverArtPanel /></TabsContent>
          <TabsContent value="retro-review" className="m-0"><RetroactiveReviewPanel /></TabsContent>
          <TabsContent value="continuity-review" className="m-0"><ContinuityDriftPanel /></TabsContent>
          <TabsContent value="plot-builder" className="m-0 p-6"><PlotBuilderAdminPanel /></TabsContent>
          <TabsContent value="bulk-novel-eval" className="m-0 p-6"><BulkNovelEvalPanel /></TabsContent>
          <TabsContent value="keys"        className="m-0"><KeysPanel /></TabsContent>
          <TabsContent value="vault"       className="m-0"><SecretsVaultPanel /></TabsContent>
          <TabsContent value="collections" className="m-0"><CollectionsPanel /></TabsContent>
          <TabsContent value="seo"         className="m-0"><SeoPanel /></TabsContent>
          <TabsContent value="blog"        className="m-0"><BlogAdminPanel /></TabsContent>
          <TabsContent value="request-log" className="m-0"><RequestLogPanel /></TabsContent>
          <TabsContent value="writer-feedback" className="m-0"><WriterFeedbackPanel /></TabsContent>
          <TabsContent value="email-log" className="m-0"><AdminEmailLogPage /></TabsContent>
          <TabsContent value="landing"     className="m-0"><LandingPanel /></TabsContent>
          <TabsContent value="what-we-offer" className="m-0"><WhatWeOfferPanel /></TabsContent>
          <TabsContent value="starter-templates" className="m-0"><StarterTemplatesAdminPanel /></TabsContent>
          <TabsContent value="genre-variants" className="m-0"><GenreVariantsAdminPanel /></TabsContent>
          <TabsContent value="author-rankings" className="m-0"><AuthorRankingsPanel /></TabsContent>
          <TabsContent value="clarity"     className="m-0"><ClarityAdminPanel /></TabsContent>
          <TabsContent value="analytics"   className="m-0"><AnalyticsPanel /></TabsContent>
          <TabsContent value="human-services" className="m-0"><HumanServicesAdminPanel /></TabsContent>
          <TabsContent value="editorial-review" className="m-0"><EditorialReviewInboxPanel /></TabsContent>
          <TabsContent value="editing"     className="m-0"><EditingPanel /></TabsContent>
          <TabsContent value="royalties"   className="m-0"><RoyaltiesPanel /></TabsContent>
          <TabsContent value="marketing-bookings" className="m-0"><AdminMarketingBookings /></TabsContent>
          <TabsContent value="isbn"        className="m-0"><IsbnAdminPanel /></TabsContent>
          <TabsContent value="audiobook-voices" className="m-0"><AudiobookVoicesPanel /></TabsContent>
          <TabsContent value="elevenlabs-narration" className="m-0"><ElevenLabsNarrationPanel /></TabsContent>
          <TabsContent value="manuscript-inventory" className="m-0"><ManuscriptInventoryPanel /></TabsContent>
          <TabsContent value="ms-export"           className="m-0"><ManuscriptExportPanel /></TabsContent>
          <TabsContent value="tax-center"  className="m-0"><TaxCenterPanel /></TabsContent>
          <TabsContent value="ohas"        className="m-0"><OhasCertifyPanel /></TabsContent>
          <TabsContent value="wipo-contracts" className="m-0"><WipoContractsPanel /></TabsContent>
          <TabsContent value="author-agreements" className="m-0"><AuthorAgreementsPanel /></TabsContent>
          <TabsContent value="isbn-bank"          className="m-0"><IsbnBankPanel /></TabsContent>
          <TabsContent value="eco-filing" className="m-0"><EcoFilingPanel /></TabsContent>
          <TabsContent value="admin-users" className="m-0"><AdminUsersPanel /></TabsContent>
          <TabsContent value="permissions" className="m-0"><PermissionsPanel /></TabsContent>
          <TabsContent value="admin-overview" className="m-0"><AdminOverviewPanel /></TabsContent>
          <TabsContent value="bookkeeping" className="m-0"><BookkeepingPanel /></TabsContent>
          <TabsContent value="ledger" className="m-0"><FinancialStatementsPanel initialTab="ledger" /></TabsContent>
          <TabsContent value="balance-sheet" className="m-0"><FinancialStatementsPanel initialTab="balance" /></TabsContent>
          <TabsContent value="income-statement" className="m-0"><FinancialStatementsPanel initialTab="income" /></TabsContent>
          <TabsContent value="cash-flow" className="m-0"><FinancialStatementsPanel initialTab="cashflow" /></TabsContent>
          <TabsContent value="equity-statement" className="m-0"><FinancialStatementsPanel initialTab="equity" /></TabsContent>
          <TabsContent value="compliance" className="m-0"><CompliancePanel /></TabsContent>
          <TabsContent value="aging" className="m-0"><AgingReportPanel /></TabsContent>
          <TabsContent value="bank-recon" className="m-0"><BankReconciliationPanel /></TabsContent>
          <TabsContent value="auto-ledger" className="m-0"><AutoLedgerHealthPanel /></TabsContent>
          <TabsContent value="ap-approvals" className="m-0"><ApprovalQueuePanel /></TabsContent>
          <TabsContent value="stripe-payments" className="m-0"><StripePaymentsPanel /></TabsContent>
          <TabsContent value="inventory" className="m-0"><InventoryPanel /></TabsContent>
          <TabsContent value="author-royalty-tiers" className="m-0"><AuthorRoyaltyTiersPanel /></TabsContent>
          <TabsContent value="broadcasts" className="m-0"><BroadcastsAdminPanel /></TabsContent>
          <TabsContent value="help-manual" className="m-0"><HelpManualPanel /></TabsContent>
          <TabsContent value="tips"        className="m-0"><TipsAdminPanel /></TabsContent>
          <TabsContent value="revenue-features" className="m-0"><RevenueFeaturesPanel /></TabsContent>
          <TabsContent value="referrals"        className="m-0"><ReferralAdminPanel /></TabsContent>
          <TabsContent value="free-tier"        className="m-0"><FreeTierAdminPanel /></TabsContent>
          <TabsContent value="comparison-pages" className="m-0"><ComparisonAdminPanel /></TabsContent>
          <TabsContent value="training"    className="m-0"><TrainingAdminPanel /></TabsContent>
          <TabsContent value="media-development" className="m-0"><MediaDevelopmentPanel /></TabsContent>
          <TabsContent value="media-vault" className="m-0"><MediaVaultPanel /></TabsContent>
          <TabsContent value="marketing-venues" className="m-0"><MarketingVenueCatalogPanel /></TabsContent>
          <TabsContent value="teleplay-qa" className="m-0"><TeleplayQAPanel /></TabsContent>
          <TabsContent value="seo-config"  className="m-0"><SeoConfigAdminPanel /></TabsContent>
          <TabsContent value="feature-videos" className="m-0"><FeatureVideosAdminPanel /></TabsContent>
          <TabsContent value="training-bundles" className="m-0"><TrainingBundlesPanel /></TabsContent>
          <TabsContent value="demo-funnel" className="m-0"><DemoFunnelPanel /></TabsContent>
          <TabsContent value="audit"       className="m-0"><AuditPanel /></TabsContent>
          <TabsContent value="security"    className="m-0"><AdminSecurityPanel /></TabsContent>
          <TabsContent value="accounting"  className="m-0"><AccountingPanel /></TabsContent>
          <TabsContent value="financials"  className="m-0"><FinancialStatementsPanel /></TabsContent>
          <TabsContent value="canva-dashboard" className="m-0"><CanvaAdminPanel /></TabsContent>
          <TabsContent value="plans"       className="m-0"><SubscriptionPlansPanel /></TabsContent>
          <TabsContent value="unit-econ"   className="m-0"><UnitEconomicsPanel /></TabsContent>
          <TabsContent value="shelf-attribution" className="m-0"><ShelfAttributionPanel /></TabsContent>
          <TabsContent value="search-visibility" className="m-0"><SearchVisibilityPanel /></TabsContent>
          <TabsContent value="ai-caching"         className="m-0"><AiCachingAdminPanel /></TabsContent>
          <TabsContent value="capacity"    className="m-0"><CapacityPanel /></TabsContent>
          <TabsContent value="indie-reader" className="m-0"><IndieReaderAdminPanel /></TabsContent>
          <TabsContent value="reader-review" className="m-0"><ReaderReviewQueuePanel /></TabsContent>
          <TabsContent value="editor-workbench" className="m-0"><EditorWorkbenchPanel /></TabsContent>
          <TabsContent value="editor-seats" className="m-0"><AdminSeatsPanel /></TabsContent>
          <TabsContent value="companion-inbox" className="m-0"><CompanionInboxPanel /></TabsContent>
          <TabsContent value="reader-sync" className="m-0"><ReaderSyncPanel /></TabsContent>
          <TabsContent value="admin-inbox"     className="m-0"><AdminInboxPanel embedded /></TabsContent>
          <TabsContent value="email-workers" className="m-0"><EmailWorkersCliPanel /></TabsContent>
          <TabsContent value="it-management" className="m-0"><ItManagementPanel /></TabsContent>
          <TabsContent value="profile"     className="m-0"><ProfilePanel /></TabsContent>
          <TabsContent value="admin-guide" className="m-0"><AdminGuidePanel /></TabsContent>
          <TabsContent value="qa"          className="m-0"><QAPanel /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
